import React, { useState } from 'react';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';

import { deleteRequest, post } from 'helpers/api';

import { ConstantContactForm } from './constant_contact_form';
import { PROVIDER_DISPLAY, PROVIDER_ORDER } from './providers';
import { ProviderForm } from './provider_form';
import { Providers } from './provider_selection';
import { ScheduledEmailsDialog } from './scheduled_emails_dialog';
import type { FormValues, PendingAction, ProviderEnum, ScheduledEmail } from './types';

interface Props {
  route: string;
  reauthenticateConstantContact: boolean;
  userName: string | null;
  apiKey: string | null;
  connectedProvider: ProviderEnum | null;
  availableProviders: ProviderEnum[];
  iContactAppId: string | null;
  constantContactPromotion: string;
  scheduledEmails: ScheduledEmail[];
}

export default function EmailCredentialEdit({
  route,
  reauthenticateConstantContact,
  userName,
  apiKey,
  connectedProvider,
  availableProviders,
  iContactAppId,
  constantContactPromotion,
  scheduledEmails,
}: Props) {
  const [selectedProvider, setSelectedProvider] = useState<ProviderEnum | null>(null);
  const [values, setValues] = useState<FormValues>({
    userName: userName ?? '',
    password: '',
    apiKey: apiKey ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const orderedProviders = PROVIDER_ORDER.filter(provider => availableProviders.includes(provider));

  const display = selectedProvider ? PROVIDER_DISPLAY[selectedProvider] : null;

  const formValid = !!display && display.fields.every(field => values[field].trim() !== '');

  // Disconnecting or switching providers moves any scheduled emails to draft, so gate those
  // actions behind a confirmation when scheduled emails exist.
  function requestAction(actionLabel: PendingAction['actionLabel'], run: () => void) {
    if (scheduledEmails.length > 0) {
      setPendingAction({ actionLabel, run });
    } else {
      run();
    }
  }

  function confirmPendingAction() {
    pendingAction?.run();
    setPendingAction(null);
  }

  function resetForm() {
    setSelectedProvider(null);
    setError(false);
    setValues({ userName: userName ?? '', password: '', apiKey: apiKey ?? '' });
  }

  async function connectProvider(provider: ProviderEnum) {
    setSubmitting(true);
    setError(false);

    try {
      const body: Record<string, string> = { provider };
      PROVIDER_DISPLAY[provider].fields.forEach((field) => {
        body[field] = values[field];
      });

      const data = await post(route, { emailCredential: body });

      if (data.authorized) {
        setSuccess(true);
        window.location.reload();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }

  async function disconnect() {
    setSubmitting(true);

    try {
      await deleteRequest(route);
      window.location.reload();
    } catch {
      showSnackbar({ text: 'Something went wrong disconnecting your email provider.', variant: 'danger' });
      setSubmitting(false);
    }
  }

  function handleProviderSelect(provider: ProviderEnum) {
    if (PROVIDER_DISPLAY[provider].flow === 'buildout') {
      requestAction('Change', () => connectProvider(provider));
    } else {
      setError(false);
      setSelectedProvider(provider);
    }
  }

  return (
    <div>
      {reauthenticateConstantContact && (
        <Alert className="mb-4" severity="warning">
          <Alert.Title>Reconnect your Constant Contact account</Alert.Title>
          <p className="mb-2">
            We need you to reauthenticate Buildout and Constant Contact to ensure your accounts are communicating
            correctly. Failure to reconnect your account will cause your scheduled emails to fail, and you will not be
            able to send out new email campaigns.
          </p>
          <ConstantContactForm route={route}>
            <div className="text-end">
              <Button type="submit" variant="primary">Connect Your Account</Button>
            </div>
          </ConstantContactForm>
        </Alert>
      )}

      {selectedProvider && display ? (
        <ProviderForm
          connectProvider={connectProvider}
          display={display}
          error={error}
          formValid={formValid}
          iContactAppId={iContactAppId}
          requestAction={requestAction}
          route={route}
          selectedProvider={selectedProvider}
          setValues={setValues}
          submitting={submitting}
          success={success}
          values={values}
          onCancel={resetForm}
        />
      ) : (
        <Providers
          connectedProvider={connectedProvider}
          constantContactPromotion={constantContactPromotion}
          orderedProviders={orderedProviders}
          onDisconnect={() => requestAction('Disconnect', disconnect)}
          onSelectProvider={handleProviderSelect}
        />
      )}

      <ScheduledEmailsDialog
        pendingAction={pendingAction}
        scheduledEmails={scheduledEmails}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmPendingAction}
      />
    </div>
  );
}
